import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import PlayerSelector from "../components/Players/PlayerSelector"; // Pfad zu PlayerSelector
import {
  fetchAllowedContent,
  fetchFeeds,
  startFeed,
  createFeed,
  updateFeedContents,
  renameFeed,
  deleteFeed,
  fetchContentConfigByFileNames,
  getThumbnailBlobUrl,
  ContentConfig,
} from "../api/api";
import {
  Box,
  Button,
  Typography,
  Paper,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Avatar,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  IconButton,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ImageIcon from "@mui/icons-material/Image";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { useTranslation } from "react-i18next";

// Globale Konstante für Mehrfachzuordnung
const ALLOW_MULTIPLE_ASSIGNMENT = false;

// Globaler Cache für Thumbnails (persistiert über Komponenten-Mounts hinweg)
const thumbnailCache = new Map<string, string>();
const thumbnailErrorCache = new Set<string>();
const thumbnailLoadAttemptsCache = new Map<string, number>();
const thumbnailLoadingCache = new Set<string>(); // Verhindert gleichzeitige Anfragen für dasselbe Thumbnail

type Feed = {
  id: string;
  external_name: string;
  content_ids: string[];
  content_metadata?: { [fileName: string]: { display_duration?: number } };
};

type Content = {
  file_name: string;
  content_name: string;
  mediatype?: string;
  thumbnail?: string;
  name?: string;
  show_captions?: boolean;
  super_category?: string;
  category?: string;
  hasRcConfig?: boolean; // Flag, ob ein rc_config-Eintrag vorhanden ist
  display_duration?: number; // Anzeigedauer in Millisekunden (für Bilder)
  duration?: number; // Wiedergabedauer in Millisekunden (für Videos) - kommt vom Backend
};

type FeedsProps = {
  adminPlayer?: string;
};

const Feeds: React.FC<FeedsProps> = ({ adminPlayer }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(
    adminPlayer || null
  );
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [selectedFeedId, setSelectedFeedId] = useState<string | null>(null);
  const [selectedFeedName, setSelectedFeedName] = useState<string | null>(null);

  const [allContent, setAllContent] = useState<Content[]>([]);
  const [available, setAvailable] = useState<Content[]>([]);
  const [feedContents, setFeedContents] = useState<Content[]>([]);
  const [snackbarMsg, setSnackbarMsg] = useState<string | null>(null);
  
  // Thumbnail States
  const [thumbnailBlobUrls, setThumbnailBlobUrls] = useState<Map<string, string>>(new Map());
  const [thumbnailImageErrors, setThumbnailImageErrors] = useState<Set<string>>(new Set());
  const [thumbnailLoadAttempts, setThumbnailLoadAttempts] = useState<Map<string, number>>(new Map());
  const [showThumbnails, setShowThumbnails] = useState<boolean>(true);
  
  // State für Anzeigedauer pro Content-Item (in Millisekunden)
  const [displayDurations, setDisplayDurations] = useState<Map<string, number>>(new Map());

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<"create" | "rename">("create");
  const [feedName, setFeedName] = useState<string>("");
  const [renameFeedId, setRenameFeedId] = useState<string | null>(null);
  
  // States für Bestätigungsdialog beim Löschen
  const [confirmDeleteFeedDialogOpen, setConfirmDeleteFeedDialogOpen] = useState(false);
  const [feedToDelete, setFeedToDelete] = useState<{ id: string; name: string } | null>(null);
  
  // State für ausgewählten Tab in "Verfügbare Inhalte"
  const [selectedSuperCategoryTab, setSelectedSuperCategoryTab] = useState<number>(0);

  // Hilfsfunktion zum Sortieren der Feeds
  const sortFeeds = (feedsArray: Feed[]) => {
    return [...feedsArray].sort((a, b) =>
      a.external_name.localeCompare(b.external_name)
    );
  };

  // Spielerwechsel setzt alles zurück
  useEffect(() => {
    console.log("--- useEffect [adminPlayer] triggered ---");
    console.log("Current adminPlayer:", adminPlayer);
    if (adminPlayer) {
      setSelectedPlayer(adminPlayer);
    } else {
      setSelectedPlayer(null);
    }
  }, [adminPlayer]);

  // Feeds laden
  useEffect(() => {
    const loadFeeds = async () => {
      console.log("--- useEffect [selectedPlayer] (loadFeeds) triggered ---");
      console.log("Current selectedPlayer for fetching feeds:", selectedPlayer);

      if (selectedPlayer) {
        try {
          const fetchedFeeds = await fetchFeeds(selectedPlayer);
          const sortedFeeds = sortFeeds(fetchedFeeds);
          setFeeds(sortedFeeds);
          console.log("Fetched and sorted feeds:", sortedFeeds);

          let newSelectedFeedId: string | null = null;
          let newSelectedFeedName: string | null = null;

          if (sortedFeeds.length > 0) {
            const currentSelected = sortedFeeds.find(
              (f: Feed) => f.id === selectedFeedId
            );
            if (currentSelected) {
              newSelectedFeedId = currentSelected.id;
              newSelectedFeedName = currentSelected.external_name;
            } else {
              newSelectedFeedId = sortedFeeds[0].id;
              newSelectedFeedName = sortedFeeds[0].external_name;
            }
          }
          setSelectedFeedId(newSelectedFeedId);
          setSelectedFeedName(newSelectedFeedName);
          console.log("New selectedFeedId after fetch:", newSelectedFeedId);
          console.log("New selectedFeedName after fetch:", newSelectedFeedName);
        } catch (error) {
          console.error("Error fetching feeds:", error);
          setSnackbarMsg(t("feeds.snackbar.errorLoadingFeeds"));
        }
      } else {
        setFeeds([]);
        setSelectedFeedId(null);
        setSelectedFeedName(null);
        console.log("selectedPlayer is null, resetting feeds/selection.");
      }
    };
    loadFeeds();
  }, [selectedPlayer, selectedFeedId, t]);

  // Content laden und rc_config-Daten abrufen
  useEffect(() => {
    const loadAllContent = async () => {
      console.log(
        "--- useEffect [selectedPlayer] (loadAllContent) triggered ---"
      );
      console.log("Current selectedPlayer for fetching content:", selectedPlayer);
      if (selectedPlayer) {
        try {
          const resp = await fetchAllowedContent(selectedPlayer);
          const all: Content[] = Array.isArray(resp) ? resp : resp.allowed || [];
          
          // rc_config-Daten für alle file_names abrufen
          if (all.length > 0) {
            try {
              const fileNames = all.map(c => c.file_name);
              const configData = await fetchContentConfigByFileNames(fileNames, selectedPlayer);
              
              // Map für schnellen Zugriff auf rc_config-Daten nach file_name
              const configMap = new Map<string, ContentConfig>();
              configData.forEach(config => {
                if (config.file_name) {
                  configMap.set(config.file_name, config);
                }
              });
              
              // Content-Items mit rc_config-Daten anreichern
              const enrichedContent = all.map(content => {
                const config = configMap.get(content.file_name);
                if (config) {
                  return {
                    ...content,
                    thumbnail: config.thumbnail || content.thumbnail,
                    name: config.name || content.content_name,
                    show_captions: config.show_captions || false,
                    super_category: config.super_category,
                    category: config.category,
                    hasRcConfig: true, // rc_config-Eintrag vorhanden
                    // duration beibehalten (wird bereits aus contents-Tabelle geladen)
                  };
                }
                // Wenn kein rc_config-Eintrag gefunden, verwende "Sonstige" als super_category
                return {
                  ...content,
                  super_category: t("feeds.otherCategory"),
                  category: undefined,
                  hasRcConfig: false, // Kein rc_config-Eintrag vorhanden
                  // duration beibehalten (wird bereits aus contents-Tabelle geladen)
                };
              });
              
              setAllContent(enrichedContent);
              console.log("Fetched and enriched allContent:", enrichedContent);
            } catch (configError) {
              console.error("Error fetching rc_config data:", configError);
              // Fallback: Verwende Original-Daten ohne rc_config
              setAllContent(all);
            }
          } else {
            setAllContent(all);
          }
        } catch (error) {
          console.error("Error fetching allowed content:", error);
          setSnackbarMsg(t("feeds.snackbar.errorLoadingContent"));
        }
      } else {
        setAllContent([]);
        console.log("selectedPlayer is null, resetting allContent.");
      }
    };
    loadAllContent();
  }, [selectedPlayer, t]);

  // Berechne Super-Categories mit useMemo und serialisiere sie für Vergleich
  // Extrahiere Super-Categories direkt aus available, ohne groupAvailableContent zu verwenden
  // (um Hoisting-Probleme zu vermeiden)
  const { superCategories, superCategoriesString } = useMemo(() => {
    // Sammle alle eindeutigen super_category Werte
    const superCatsSet = new Set<string | null>();
    available.forEach(item => {
      superCatsSet.add(item.super_category || null);
    });
    
    // Sortiere Super-Categories ("Sonstige" immer zuletzt, dann null, dann alphabetisch)
    const otherCategoryLabel = t("feeds.otherCategory");
    const superCats = Array.from(superCatsSet).sort((a, b) => {
      // "Sonstige" immer zuletzt
      if (a === otherCategoryLabel) return 1;
      if (b === otherCategoryLabel) return -1;
      // null zuletzt (aber vor "Sonstige")
      if (a === null) return 1;
      if (b === null) return -1;
      // Alphabetisch sortieren
      return a.localeCompare(b);
    });
    
    return {
      superCategories: superCats,
      superCategoriesString: JSON.stringify(superCats)
    };
  }, [available, t]);

  // Tab-Index zurücksetzen, nur wenn sich die Struktur der Super-Categories ändert
  // (nicht bei jeder Änderung der Inhalte, um den Tab-Fokus beim Drag-and-Drop zu erhalten)
  const prevSuperCategoriesStringRef = React.useRef<string>("");
  useEffect(() => {
    // Nur zurücksetzen, wenn sich die Struktur der Super-Categories geändert hat
    if (prevSuperCategoriesStringRef.current !== superCategoriesString) {
      prevSuperCategoriesStringRef.current = superCategoriesString;
      // Prüfe, ob der aktuelle Tab-Index noch gültig ist
      if (selectedSuperCategoryTab >= superCategories.length && superCategories.length > 0) {
        setSelectedSuperCategoryTab(0);
      }
    }
  }, [superCategoriesString, superCategories.length, selectedSuperCategoryTab]);

  // Feed-Inhalte/Verfügbare berechnen
  useEffect(() => {
    console.log("--- useEffect [selectedFeedId, feeds, allContent] triggered ---");
    console.log("Current selectedFeedId:", selectedFeedId);
    console.log("Current feeds (length):", feeds.length);
    console.log("Current allContent (length):", allContent.length);

    if (!selectedFeedId || feeds.length === 0 || allContent.length === 0) {
      setFeedContents([]);
      setAvailable([]);
      console.log("Conditions not met for calculating feed contents/available.");
      return;
    }

    const feed = feeds.find((f: Feed) => f.id === selectedFeedId);
    console.log("Found feed:", feed);

    if (feed) {
      setSelectedFeedName(feed.external_name);
      console.log("Set selectedFeedName to:", feed.external_name);

      const feedIds = feed.content_ids ?? [];
      console.log("Feed content_ids:", feedIds);

      const sortedFeedContents: Content[] = [];
      const newDisplayDurations = new Map<string, number>();
      feedIds.forEach((id: string, index: number) => {
        const contentItem = allContent.find((c: Content) => c.file_name === id);
        if (contentItem) {
          // Initialisiere Anzeigedauer aus Feed-Metadaten, falls vorhanden
          // Bei Mehrfachzuordnung: Prüfe Metadaten mit Index
          const metadataKey = ALLOW_MULTIPLE_ASSIGNMENT ? `${id}-${index}` : id;
          const metadata = feed.content_metadata?.[metadataKey] || feed.content_metadata?.[id];
          // Nur wenn Metadaten vorhanden sind, wird die Dauer in displayDurations gesetzt
          // contentItem.display_duration wird nicht verwendet, da dies nur ein Standardwert sein könnte
          if (metadata?.display_duration) {
            // Verwende eindeutige ID basierend auf Index
            const itemId = `feedContents-${id}-${index}`;
            newDisplayDurations.set(itemId, metadata.display_duration);
            contentItem.display_duration = metadata.display_duration;
          }
          sortedFeedContents.push(contentItem);
        } else {
          console.warn(
            `Content item with file_name ${id} (from feed.content_ids) not found in allContent.`
          );
        }
      });
      setFeedContents(sortedFeedContents);
      // Aktualisiere displayDurations State mit geladenen Werten
      // Wichtig: Nur Werte aus Metadaten werden gespeichert, nicht Standardwerte
      setDisplayDurations(prev => {
        // Erstelle eine neue Map nur mit den Werten aus newDisplayDurations
        // Entferne alte Werte, die nicht mehr in den Metadaten sind
        const merged = new Map<string, number>();
        newDisplayDurations.forEach((value, key) => {
          merged.set(key, value);
        });
        return merged;
      });
      console.log("Calculated feedContents:", sortedFeedContents);

      // Wenn Mehrfachzuordnung erlaubt ist, werden alle Inhalte als verfügbar angezeigt
      // Ansonsten werden nur die Inhalte angezeigt, die nicht in der Playlist sind
      const currentAvailable = ALLOW_MULTIPLE_ASSIGNMENT
        ? allContent
        : allContent.filter((c: Content) => !feedIds.includes(c.file_name));
      setAvailable(currentAvailable);
      console.log("Calculated available content:", currentAvailable);
    } else {
      setSelectedFeedId(null);
      setSelectedFeedName(null);
      setFeedContents([]);
      setAvailable([]);
      console.warn("Selected feed not found in current feeds list. Resetting selection.");
    }
  }, [selectedFeedId, feeds, allContent]);

  const updateFeedContentsState = useCallback(
    async (feedIdToUpdate: string, newFeedContentFileNames: string[]) => {
      console.log("--- updateFeedContentsState triggered ---");
      console.log(
        "Updating feed:",
        feedIdToUpdate,
        "with content IDs:",
        newFeedContentFileNames
      );
      if (selectedPlayer && feedIdToUpdate) {
        try {
          // Erstelle contentMetadata aus displayDurations
          // Bei Mehrfachzuordnung: Metadaten pro Position (Index) speichern
          const contentMetadata: { [fileName: string]: { display_duration?: number } } = {};
          
          // Iteriere über die neuen Content-FileNames und hole die Anzeigedauer für jedes Item basierend auf Index
          newFeedContentFileNames.forEach((fileName, index) => {
            const itemId = `feedContents-${fileName}-${index}`;
            const duration = displayDurations.get(itemId);
            if (duration && duration > 0) {
              // Bei Mehrfachzuordnung: Verwende eine eindeutige ID pro Position
              const metadataKey = ALLOW_MULTIPLE_ASSIGNMENT 
                ? `${fileName}-${index}`
                : fileName;
              contentMetadata[metadataKey] = { display_duration: duration };
            }
          });
          
          await updateFeedContents(
            selectedPlayer,
            feedIdToUpdate,
            newFeedContentFileNames,
            Object.keys(contentMetadata).length > 0 ? contentMetadata : undefined
          );
          setSnackbarMsg(t("feeds.snackbar.feedUpdated"));

          const updatedFeeds = await fetchFeeds(selectedPlayer);
          const sortedFeeds = sortFeeds(updatedFeeds);
          setFeeds(sortedFeeds);
          console.log("Feeds re-fetched and sorted after update.");
        } catch (error) {
          console.error("Fehler beim Aktualisieren des Feeds:", error);
          setSnackbarMsg(t("feeds.snackbar.errorUpdatingFeed"));
        }
      }
    },
    [selectedPlayer, displayDurations, t]
  );

  const onDragEnd = (result: DropResult) => {
    console.log("--- onDragEnd triggered ---");
    console.log("Drag Result:", result);

    const { source, destination, draggableId } = result;

    if (!destination) {
      console.log("Dropped outside a droppable area.");
      return;
    }

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      console.log("Dropped in the same position.");
      return;
    }

    // Extrahiere file_name aus draggableId
    // Format kann sein: "available-{file_name}-{index}" oder "feedContents-{file_name}-{index}"
    let file_name: string;
    if (draggableId.startsWith("available-")) {
      // Format: "available-{file_name}-{index}"
      const withoutPrefix = draggableId.substring("available-".length);
      const lastDashIndex = withoutPrefix.lastIndexOf("-");
      if (lastDashIndex !== -1) {
        file_name = withoutPrefix.substring(0, lastDashIndex);
      } else {
        file_name = withoutPrefix;
      }
    } else if (draggableId.startsWith("feedContents-")) {
      // Format: "feedContents-{file_name}-{index}"
      const withoutPrefix = draggableId.substring("feedContents-".length);
      const lastDashIndex = withoutPrefix.lastIndexOf("-");
      if (lastDashIndex !== -1) {
        file_name = withoutPrefix.substring(0, lastDashIndex);
      } else {
        file_name = withoutPrefix;
      }
    } else {
      // Fallback für alte Formatierung
      file_name = draggableId;
    }

    const movedItem = allContent.find((c: Content) => c.file_name === file_name);

    if (!movedItem) {
      console.error("Moved item not found in allContent. This should not happen.");
      return;
    }

    let newFeedContents = Array.from(feedContents);
    let newAvailable = Array.from(available);
    let updatedFeedContentFileNames: string[] = [];

    if (source.droppableId === "feedContents" && destination.droppableId === "feedContents") {
      const [removed] = newFeedContents.splice(source.index, 1);
      newFeedContents.splice(destination.index, 0, removed);
      setFeedContents(newFeedContents);
      updatedFeedContentFileNames = newFeedContents.map((c) => c.file_name);
      console.log("Reordered feedContents locally:", newFeedContents);
    } else if (source.droppableId === "available" && destination.droppableId === "feedContents") {
      // Wenn Mehrfachzuordnung erlaubt ist, Item nicht aus available entfernen
      // Item bleibt immer in available, auch wenn es gedraggt wird
      // Item zu feedContents hinzufügen (auch wenn es bereits vorhanden ist)
      newFeedContents.splice(destination.index, 0, movedItem);

      setFeedContents(newFeedContents);

      updatedFeedContentFileNames = newFeedContents.map((c) => c.file_name);
      console.log(
        "Moved item from available to feedContents. New feedContents:",
        newFeedContents
      );
      console.log("Multiple assignment allowed - item remains in available");
    } else if (source.droppableId === "feedContents" && destination.droppableId === "available") {
      const [removed] = newFeedContents.splice(source.index, 1);
      
      // Wenn Mehrfachzuordnung erlaubt ist, Item nicht zu available hinzufügen (ist bereits dort)
      // Ansonsten Item zu available hinzufügen
      if (!ALLOW_MULTIPLE_ASSIGNMENT) {
        newAvailable.splice(destination.index, 0, removed);
        setAvailable(newAvailable);
      }

      setFeedContents(newFeedContents);

      updatedFeedContentFileNames = newFeedContents.map((c) => c.file_name);
      console.log(
        "Moved item from feedContents to available. New feedContents:",
        newFeedContents
      );
      if (!ALLOW_MULTIPLE_ASSIGNMENT) {
        console.log("New available:", newAvailable);
      } else {
        console.log("Multiple assignment allowed - item already in available");
      }
    }

    if (
      selectedFeedId &&
      (updatedFeedContentFileNames.length > 0 ||
        (source.droppableId === "feedContents" && newFeedContents.length === 0))
    ) {
      updateFeedContentsState(selectedFeedId, updatedFeedContentFileNames);
    }
  };

  const handleStartFeed = async () => {
    console.log("--- handleStartFeed triggered ---");
    if (selectedPlayer && selectedFeedId && selectedFeedName) {
      try {
        // Sicherstellen, dass die aktuelle Reihenfolge gespeichert ist
        const currentContentFileNames = feedContents.map((c) => c.file_name);
        if (currentContentFileNames.length > 0) {
          // Aktualisiere die Reihenfolge im Feed, falls sie sich geändert hat
          await updateFeedContentsState(selectedFeedId, currentContentFileNames);
        }
        
        await startFeed(selectedPlayer, selectedFeedId, selectedFeedName);
        setSnackbarMsg(t("feeds.snackbar.feedStarted"));
        console.log("Feed started successfully.");
      } catch (error) {
        console.error("Fehler beim Starten des Feeds:", error);
        setSnackbarMsg(t("feeds.snackbar.errorStartingFeed"));
      }
    } else {
      setSnackbarMsg(t("feeds.snackbar.noFeedSelectedToStart"));
      console.warn(
        "Cannot start feed: Missing selectedPlayer, selectedFeedId, or selectedFeedName."
      );
    }
  };

  const handleCreateFeed = async () => {
    console.log("--- handleCreateFeed triggered ---");
    if (selectedPlayer && feedName.trim()) {
      try {
        await createFeed(selectedPlayer, feedName.trim(), []);
        const fetchedFeeds = await fetchFeeds(selectedPlayer);
        const sortedFeeds = sortFeeds(fetchedFeeds);
        setFeeds(sortedFeeds);
        console.log("Feeds re-fetched and sorted after creation:", sortedFeeds);

        const newFeed = sortedFeeds.find(
          (feed: Feed) => feed.external_name === feedName.trim()
        );
        if (newFeed) {
          setSelectedFeedId(newFeed.id);
          setSelectedFeedName(newFeed.external_name);
          console.log("Selected newly created feed:", newFeed.external_name);
        } else if (sortedFeeds.length > 0) {
          setSelectedFeedId(sortedFeeds[0].id);
          setSelectedFeedName(sortedFeeds[0].external_name);
          console.log(
            "New feed not found by name, selected first feed:",
            sortedFeeds[0].external_name
          );
        } else {
          setSelectedFeedId(null);
          setSelectedFeedName(null);
          console.log("No feeds available after creation.");
        }

        setDialogOpen(false);
        setFeedName("");
        setSnackbarMsg(t("feeds.snackbar.feedCreated"));
      } catch (error) {
        console.error("Fehler beim Erstellen des Feeds:", error);
        setSnackbarMsg(t("feeds.snackbar.errorCreatingFeed"));
      }
    } else {
      setSnackbarMsg(t("feeds.snackbar.enterFeedName"));
      console.warn("Attempted to create feed with no player or empty name.");
    }
  };

  const handleRenameFeed = async () => {
    console.log("--- handleRenameFeed triggered ---");
    if (selectedPlayer && renameFeedId && feedName.trim()) {
      try {
        await renameFeed(selectedPlayer, renameFeedId, feedName.trim());
        const fetchedFeeds = await fetchFeeds(selectedPlayer);
        const sortedFeeds = sortFeeds(fetchedFeeds);
        setFeeds(sortedFeeds);
        console.log("Feeds re-fetched and sorted after rename:", sortedFeeds);

        if (selectedFeedId === renameFeedId) {
          setSelectedFeedName(feedName.trim());
          console.log("Updated selectedFeedName to:", feedName.trim());
        }
        setDialogOpen(false);
        setFeedName("");
        setRenameFeedId(null);
        setSnackbarMsg(t("feeds.snackbar.feedRenamed"));
      } catch (error) {
        console.error("Fehler beim Umbenennen des Feeds:", error);
        setSnackbarMsg(t("feeds.snackbar.errorRenamingFeed"));
      }
    } else {
      setSnackbarMsg(t("feeds.snackbar.enterFeedName"));
      console.warn("Attempted to rename feed with no player, ID, or empty name.");
    }
  };

  const handleDeleteFeed = (feedId: string) => {
    const feed = feeds.find((f: Feed) => f.id === feedId);
    if (feed) {
      setFeedToDelete({ id: feedId, name: feed.external_name });
      setConfirmDeleteFeedDialogOpen(true);
    }
  };

  const handleConfirmDeleteFeed = async () => {
    if (!selectedPlayer || !feedToDelete) return;

    try {
      await deleteFeed(selectedPlayer, feedToDelete.id);
      const fetchedFeeds = await fetchFeeds(selectedPlayer);
      const sortedFeeds = sortFeeds(fetchedFeeds);
      setFeeds(sortedFeeds);
      console.log("Feeds re-fetched and sorted after deletion:", sortedFeeds);

      if (selectedFeedId === feedToDelete.id) {
        setSelectedFeedId(sortedFeeds.length > 0 ? sortedFeeds[0].id : null);
        setSelectedFeedName(
          sortedFeeds.length > 0 ? sortedFeeds[0].external_name : null
        );
        console.log(
          "Deleted selected feed. New selection:",
          sortedFeeds.length > 0 ? sortedFeeds[0].external_name : "none"
        );
      } else if (
        sortedFeeds.length > 0 &&
        !sortedFeeds.some((feed: Feed) => feed.id === selectedFeedId)
      ) {
        setSelectedFeedId(sortedFeeds[0].id);
        setSelectedFeedName(sortedFeeds[0].external_name);
        console.log(
          "Previously selected feed no longer exists. Selecting first feed:",
          sortedFeeds[0].external_name
        );
      }
      setSnackbarMsg(t("feeds.snackbar.feedDeleted"));
      setConfirmDeleteFeedDialogOpen(false);
      setFeedToDelete(null);
    } catch (error) {
      console.error("Fehler beim Löschen des Feeds:", error);
      setSnackbarMsg(t("feeds.snackbar.errorDeletingFeed"));
    }
  };

  const handleCancelDeleteFeed = () => {
    setConfirmDeleteFeedDialogOpen(false);
    setFeedToDelete(null);
  };

  const listStyle: React.CSSProperties = {
    minHeight: 250,
    background: "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 8,
    padding: 8,
    transition: "background 0.2s",
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
    gridAutoRows: "min-content",
    gap: 8,
    alignContent: "start",
  };

  const groupedListStyle: React.CSSProperties = {
    minHeight: 250,
    background: "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 8,
    padding: 8,
    transition: "background 0.2s",
    display: "block",
  };

  const itemStyle = (isDragging: boolean): React.CSSProperties => ({
    userSelect: "none",
    background: isDragging ? "#eeeeee" : "#ffffff",
    border: "1px solid rgba(0, 0, 0, 0.1)",
    borderRadius: 6,
    transition: "background 0.18s",
    boxShadow: isDragging ? "0 2px 12px rgba(0, 0, 0, 0.1)" : "none",
    padding: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 8,
    alignSelf: "start",
    // Items sollen während des Draggens sichtbar bleiben
    opacity: isDragging && ALLOW_MULTIPLE_ASSIGNMENT ? 0.5 : 1,
  });

  // Thumbnail laden (ähnlich wie in ContentConfigPanel.tsx)
  const loadThumbnail = useCallback(async (filename: string) => {
    if (!filename) return null;
    
    // Prüfen, ob bereits im globalen Cache
    if (thumbnailCache.has(filename)) {
      const cachedUrl = thumbnailCache.get(filename)!;
      // Synchronisiere mit lokalem State
      setThumbnailBlobUrls(prev => {
        if (!prev.has(filename)) {
          return new Map(prev).set(filename, cachedUrl);
        }
        return prev;
      });
      return cachedUrl;
    }
    
    // Prüfen, ob bereits geladen (lokal)
    if (thumbnailBlobUrls.has(filename)) {
      return thumbnailBlobUrls.get(filename) || null;
    }
    
    // Prüfen, ob bereits ein Fehler im Cache ist
    if (thumbnailErrorCache.has(filename)) {
      setThumbnailImageErrors(prev => {
        if (!prev.has(filename)) {
          return new Set(prev).add(filename);
        }
        return prev;
      });
      return null;
    }
    
    // Prüfen, ob bereits ein Fehler vorliegt (lokal)
    if (thumbnailImageErrors.has(filename)) {
      return null;
    }
    
    // Prüfen, ob bereits zu viele Versuche unternommen wurden (im Cache)
    const cachedAttempts = thumbnailLoadAttemptsCache.get(filename) || 0;
    const localAttempts = thumbnailLoadAttempts.get(filename) || 0;
    const attempts = Math.max(cachedAttempts, localAttempts);
    
    if (attempts >= 3) {
      thumbnailErrorCache.add(filename);
      setThumbnailImageErrors(prev => new Set(prev).add(filename));
      return null;
    }
    
    // Prüfen, ob bereits ein Ladeversuch läuft
    if (thumbnailLoadingCache.has(filename)) {
      return null;
    }
    
    // Ladeversuch markieren
    thumbnailLoadingCache.add(filename);
    
    // Versuch zählen
    const newAttempts = attempts + 1;
    setThumbnailLoadAttempts(prev => new Map(prev).set(filename, newAttempts));
    thumbnailLoadAttemptsCache.set(filename, newAttempts);
    
    try {
      const blobUrl = await getThumbnailBlobUrl(filename);
      if (blobUrl) {
        // In globalen Cache speichern
        thumbnailCache.set(filename, blobUrl);
        setThumbnailBlobUrls(prev => new Map(prev).set(filename, blobUrl));
        // Bei Erfolg Versuche zurücksetzen
        setThumbnailLoadAttempts(prev => {
          const newMap = new Map(prev);
          newMap.delete(filename);
          return newMap;
        });
        thumbnailLoadAttemptsCache.delete(filename);
        thumbnailImageErrors.delete(filename);
        thumbnailErrorCache.delete(filename);
      }
      thumbnailLoadingCache.delete(filename);
      return blobUrl;
    } catch (error) {
      console.error('Fehler beim Laden des Thumbnails:', error);
      thumbnailLoadingCache.delete(filename);
      // Bei Fehler: Wenn 3 Versuche erreicht, zu Fehlerliste hinzufügen
      if (newAttempts >= 3) {
        thumbnailErrorCache.add(filename);
        setThumbnailImageErrors(prev => new Set(prev).add(filename));
      }
      return null;
    }
  }, [thumbnailBlobUrls, thumbnailImageErrors, thumbnailLoadAttempts]);

  // Hilfsfunktion: Medientyp formatieren (übersetzt, in Klammern) - nur für linke Box
  const formatMediaType = (mediatype?: string): string => {
    if (!mediatype) return "";
    const lowerType = mediatype.toLowerCase();
    const translated = t(`feeds.mediaTypes.${lowerType}`, { defaultValue: lowerType });
    // Erster Buchstabe groß machen
    const capitalized = translated.charAt(0).toUpperCase() + translated.slice(1);
    return `(${capitalized})`;
  };

  // Hilfsfunktion: Prüft, ob ein Content-Item ein Bild ist
  const isImage = (item: Content): boolean => {
    return item.mediatype?.toLowerCase() === 'image';
  };

  // Hilfsfunktion: Prüft, ob ein Content-Item ein Video ist
  const isVideo = (item: Content): boolean => {
    return item.mediatype?.toLowerCase() === 'video';
  };

  // Hilfsfunktion: Formatiere Dauer in MM:SS Format (für Videos - erwartet Millisekunden)
  const formatDuration = (durationInMs: number): string => {
    if (!durationInMs || durationInMs <= 0) return '0:00';
    
    // Videos: duration kommt jetzt in Millisekunden vom Backend
    const totalSeconds = Math.floor(durationInMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  // Hilfsfunktion: Formatiere Dauer in MM:SS Format (für Gesamtdauer - erwartet Millisekunden)
  const formatDurationFromMs = (durationInMs: number): string => {
    if (!durationInMs || durationInMs <= 0) return '0:00';
    
    // Gesamtdauer: wird in Millisekunden berechnet
    const totalSeconds = Math.floor(durationInMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };


  // Berechne Gesamtdauer der Playlist
  // Gibt null zurück, wenn nur ein Bild vorhanden ist und keine Anzeigedauer gesetzt ist (unbegrenzt)
  const calculateTotalDuration = useCallback((): number | null => {
    // Prüfe, ob nur ein Bild vorhanden ist und keine Anzeigedauer gesetzt ist
    if (feedContents.length === 1 && isImage(feedContents[0])) {
      const item = feedContents[0];
      const itemId = `feedContents-${item.file_name}-0`;
      // Nur prüfen, ob die Dauer explizit in displayDurations gespeichert ist
      // Wenn nicht, ist es "unbegrenzt" (auch wenn item.display_duration einen Wert hat)
      const duration = displayDurations.get(itemId);
      // Wenn keine Dauer explizit gesetzt ist, ist es unbegrenzt
      if (!duration || duration === 0) {
        return null; // null bedeutet "unbegrenzt"
      }
    }
    
    let totalMs = 0;
    feedContents.forEach((item, index) => {
      if (isVideo(item)) {
        // Für Videos: duration ist jetzt bereits in Millisekunden
        const durationMs = item.duration || 0;
        totalMs += durationMs;
      } else if (isImage(item)) {
        // Für Bilder: Verwende display_duration nur aus displayDurations Map (bereits in Millisekunden)
        // Nur wenn explizit in displayDurations gespeichert, wurde sie manuell gesetzt
        const itemId = `feedContents-${item.file_name}-${index}`;
        const duration = displayDurations.get(itemId);
        // Nur hinzufügen, wenn eine Dauer explizit gesetzt ist
        if (duration && duration > 0) {
          totalMs += duration;
        }
      }
    });
    return totalMs;
  }, [feedContents, displayDurations]);

  // Hilfsfunktion: Millisekunden in Minuten und Sekunden umwandeln
  const msToMinutesSeconds = (ms: number): { minutes: number; seconds: number } => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return { minutes, seconds };
  };

  // Hilfsfunktion: Minuten und Sekunden in Millisekunden umwandeln
  const minutesSecondsToMs = (minutes: number, seconds: number): number => {
    return (minutes * 60 + seconds) * 1000;
  };

  // Handler für Anzeigedauer-Änderung
  // itemId: eindeutige ID für das Item (z.B. "feedContents-{file_name}-{index}")
  const handleDisplayDurationChange = (itemId: string, minutes: number, seconds: number) => {
    const ms = minutesSecondsToMs(minutes, seconds);
    setDisplayDurations(prev => {
      const newMap = new Map(prev);
      if (ms > 0) {
        newMap.set(itemId, ms);
      } else {
        newMap.delete(itemId);
      }
      return newMap;
    });
    // Aktualisiere auch den feedContents State basierend auf dem Index
    // Extrahiere Index aus itemId (Format: "feedContents-{file_name}-{index}")
    const match = itemId.match(/^feedContents-.*-(\d+)$/);
    if (match) {
      const index = parseInt(match[1], 10);
      setFeedContents(prev => prev.map((item, idx) => 
        idx === index 
          ? { ...item, display_duration: ms > 0 ? ms : undefined }
          : item
      ));
    }
  };

  // Speichere Anzeigedauer-Metadaten in der Datenbank
  const saveDisplayDurationMetadata = useCallback(async () => {
    if (!selectedPlayer || !selectedFeedId || feedContents.length === 0) {
      return;
    }

    try {
      // Erstelle contentMetadata aus displayDurations
      // Bei Mehrfachzuordnung: Metadaten pro Position (Index) speichern
      const contentMetadata: { [fileName: string]: { display_duration?: number } } = {};
      const currentContentFileNames = feedContents.map((c) => c.file_name);
      
      // Iteriere über feedContents und hole die Anzeigedauer für jedes Item basierend auf Index
      feedContents.forEach((item, index) => {
        const itemId = `feedContents-${item.file_name}-${index}`;
        const duration = displayDurations.get(itemId);
        if (duration && duration > 0) {
          // Bei Mehrfachzuordnung: Verwende eine eindeutige ID pro Position
          // Format: "{file_name}-{index}" als Key für Metadaten
          const metadataKey = ALLOW_MULTIPLE_ASSIGNMENT 
            ? `${item.file_name}-${index}`
            : item.file_name;
          contentMetadata[metadataKey] = { display_duration: duration };
        }
      });

      // Speichere Metadaten - immer senden, auch wenn leer (um konsistent zu bleiben)
      await updateFeedContents(
        selectedPlayer,
        selectedFeedId,
        currentContentFileNames,
        contentMetadata
      );
      
      console.log('Anzeigedauer-Metadaten gespeichert:', contentMetadata);
      
      // Aktualisiere Feeds-Liste, um die neuen Metadaten zu reflektieren
      const updatedFeeds = await fetchFeeds(selectedPlayer);
      const sortedFeeds = sortFeeds(updatedFeeds);
      setFeeds(sortedFeeds);
    } catch (error) {
      console.error('Fehler beim Speichern der Anzeigedauer-Metadaten:', error);
      setSnackbarMsg(t("feeds.snackbar.errorUpdatingFeed"));
    }
  }, [selectedPlayer, selectedFeedId, feedContents, displayDurations, t]);

  // Hilfsfunktion: Titel ermitteln (name aus rc_config oder content_name als Fallback)
  // Gibt null zurück, wenn kein Titel angezeigt werden soll
  const getDisplayTitle = (item: Content): string | null => {
    // Wenn kein rc_config-Eintrag vorhanden ist, immer content_name anzeigen
    if (!item.hasRcConfig) {
      return item.content_name;
    }
    // Wenn rc_config-Eintrag vorhanden ist: Nur anzeigen, wenn show_captions = true
    if (item.show_captions && item.name) {
      return item.name;
    }
    // Wenn show_captions = false, keinen Titel anzeigen
    return null;
  };

  // Hilfsfunktion: Verfügbare Inhalte nach super_category und category gruppieren
  const groupAvailableContent = (content: Content[]): Array<{
    superCategory: string | null;
    categories: Array<{
      category: string | null;
      items: Content[];
    }>;
  }> => {
    // Map für Gruppierung: super_category -> category -> items
    const groupedMap = new Map<string | null, Map<string | null, Content[]>>();
    
    content.forEach(item => {
      const superCat = item.super_category || null;
      const cat = item.category || null;
      
      if (!groupedMap.has(superCat)) {
        groupedMap.set(superCat, new Map());
      }
      
      const categoryMap = groupedMap.get(superCat)!;
      if (!categoryMap.has(cat)) {
        categoryMap.set(cat, []);
      }
      
      categoryMap.get(cat)!.push(item);
    });
    
    // In Array-Struktur umwandeln und sortieren
    const result: Array<{
      superCategory: string | null;
      categories: Array<{
        category: string | null;
        items: Content[];
      }>;
    }> = [];
    
    // Sortiere super_categories ("Sonstige" immer zuletzt, dann null, dann alphabetisch)
    const otherCategoryLabel = t("feeds.otherCategory");
    const sortedSuperCats = Array.from(groupedMap.keys()).sort((a, b) => {
      // "Sonstige" immer zuletzt
      if (a === otherCategoryLabel) return 1;
      if (b === otherCategoryLabel) return -1;
      // null zuletzt (aber vor "Sonstige")
      if (a === null) return 1;
      if (b === null) return -1;
      // Alphabetisch sortieren
      return a.localeCompare(b);
    });
    
    sortedSuperCats.forEach(superCat => {
      const categoryMap = groupedMap.get(superCat)!;
      const categories: Array<{ category: string | null; items: Content[] }> = [];
      
      // Sortiere categories (null zuletzt)
      const sortedCats = Array.from(categoryMap.keys()).sort((a, b) => {
        if (a === null) return 1;
        if (b === null) return -1;
        return a.localeCompare(b);
      });
      
      sortedCats.forEach(cat => {
        categories.push({
          category: cat,
          items: categoryMap.get(cat)!,
        });
      });
      
      result.push({
        superCategory: superCat,
        categories,
      });
    });
    
    return result;
  };

  // Thumbnail-Komponente für Content-Item
  const ContentThumbnail: React.FC<{ item: Content }> = ({ item }) => {
    const [loading, setLoading] = useState(false);
    // Wenn kein rc_config vorhanden ist, verwende dummy.png
    const thumbnailToUse = !item.hasRcConfig ? 'dummy.png' : item.thumbnail;
    const blobUrl = thumbnailToUse ? thumbnailBlobUrls.get(thumbnailToUse) : null;
    const hasError = thumbnailToUse ? thumbnailImageErrors.has(thumbnailToUse) : false;
    
    useEffect(() => {
      if (thumbnailToUse && !blobUrl && !hasError && showThumbnails) {
        // Für dummy.png verwende direkt den Pfad
        if (thumbnailToUse === 'dummy.png') {
          return;
        }
        
        // Prüfe zuerst den globalen Cache
        const cachedBlobUrl = thumbnailCache.get(thumbnailToUse);
        if (cachedBlobUrl) {
          setThumbnailBlobUrls(prev => {
            if (!prev.has(thumbnailToUse)) {
              return new Map(prev).set(thumbnailToUse, cachedBlobUrl);
            }
            return prev;
          });
          return;
        }
        
        // Prüfe, ob bereits ein Fehler im Cache ist
        if (thumbnailErrorCache.has(thumbnailToUse)) {
          setThumbnailImageErrors(prev => {
            if (!prev.has(thumbnailToUse)) {
              return new Set(prev).add(thumbnailToUse);
            }
            return prev;
          });
          return;
        }
        
        const attempts = thumbnailLoadAttempts.get(thumbnailToUse) || 0;
        const cachedAttempts = thumbnailLoadAttemptsCache.get(thumbnailToUse) || 0;
        if (Math.max(attempts, cachedAttempts) < 3) {
          setLoading(true);
          loadThumbnail(thumbnailToUse)
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
        }
      }
    }, [thumbnailToUse, blobUrl, hasError]);
    
    if (!showThumbnails) {
      return null;
    }
    
    // Für dummy.png verwende direkt den Pfad
    if (thumbnailToUse === 'dummy.png') {
      return (
        <Box
          component="img"
          src="/dummy.png"
          alt="dummy"
          sx={{
            width: 200,
            height: 125,
            objectFit: "cover",
            borderRadius: 1,
            border: "1px solid rgba(0, 0, 0, 0.1)",
          }}
        />
      );
    }
    
    if (loading) {
      return (
        <Avatar variant="rounded" sx={{ width: 200, height: 125 }}>
          <CircularProgress size={24} />
        </Avatar>
      );
    }
    
    if (hasError || !blobUrl) {
      return (
        <Avatar variant="rounded" sx={{ width: 200, height: 125, bgcolor: 'grey.300' }}>
          ?
        </Avatar>
      );
    }
    
    return (
      <Box
        component="img"
        src={blobUrl}
        alt={thumbnailToUse}
        sx={{
          width: 200,
          height: 125,
          objectFit: "cover",
          borderRadius: 1,
          border: "1px solid rgba(0, 0, 0, 0.1)",
        }}
      />
    );
  };

  // Beim Mount: Globale Cache-Daten in lokale States übernehmen
  useEffect(() => {
    // Thumbnails aus dem Cache in den lokalen State übernehmen
    thumbnailCache.forEach((blobUrl, filename) => {
      if (!thumbnailBlobUrls.has(filename)) {
        setThumbnailBlobUrls(prev => new Map(prev).set(filename, blobUrl));
      }
    });
    
    // Fehler aus dem Cache in den lokalen State übernehmen
    thumbnailErrorCache.forEach(filename => {
      if (!thumbnailImageErrors.has(filename)) {
        setThumbnailImageErrors(prev => new Set(prev).add(filename));
      }
    });
    
    // Versuche aus dem Cache in den lokalen State übernehmen
    thumbnailLoadAttemptsCache.forEach((attempts, filename) => {
      const currentAttempts = thumbnailLoadAttempts.get(filename) || 0;
      if (attempts > currentAttempts) {
        setThumbnailLoadAttempts(prev => new Map(prev).set(filename, attempts));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Nur beim Mount ausführen - Dependencies absichtlich weggelassen

  // Thumbnails laden, wenn sich allContent ändert und showThumbnails aktiviert ist
  useEffect(() => {
    if (!showThumbnails) {
      return;
    }
    
    const loadThumbnails = async () => {
      const thumbnailsToLoad = new Set<string>();
      
      // Sammle alle Thumbnails aus allContent
      allContent.forEach(item => {
        if (item.thumbnail) {
          // Prüfe zuerst den globalen Cache
          if (thumbnailCache.has(item.thumbnail)) {
            // Synchronisiere mit lokalem State
            setThumbnailBlobUrls(prev => {
              if (!prev.has(item.thumbnail!)) {
                return new Map(prev).set(item.thumbnail!, thumbnailCache.get(item.thumbnail!)!);
              }
              return prev;
            });
            return;
          }
          
          // Prüfe, ob bereits ein Fehler im Cache ist
          if (thumbnailErrorCache.has(item.thumbnail)) {
            setThumbnailImageErrors(prev => {
              if (!prev.has(item.thumbnail!)) {
                return new Set(prev).add(item.thumbnail!);
              }
              return prev;
            });
            return;
          }
          
          if (!thumbnailBlobUrls.has(item.thumbnail) && !thumbnailImageErrors.has(item.thumbnail)) {
            const attempts = thumbnailLoadAttempts.get(item.thumbnail) || 0;
            const cachedAttempts = thumbnailLoadAttemptsCache.get(item.thumbnail) || 0;
            if (Math.max(attempts, cachedAttempts) < 3) {
              thumbnailsToLoad.add(item.thumbnail);
            }
          }
        }
      });
      
      // Lade alle Thumbnails parallel
      const loadPromises = Array.from(thumbnailsToLoad).map(filename => loadThumbnail(filename));
      await Promise.all(loadPromises);
    };
    
    if (allContent.length > 0) {
      loadThumbnails();
    }
  }, [allContent, loadThumbnail, thumbnailBlobUrls, thumbnailImageErrors, thumbnailLoadAttempts, showThumbnails]);

  useEffect(() => {
    console.log("--- useEffect [dialogType, renameFeedId, feeds] triggered ---");
    if (dialogType === "rename" && renameFeedId) {
      const feed = feeds.find((f: Feed) => f.id === renameFeedId);
      if (feed) {
        setFeedName(feed.external_name);
        console.log("Dialog: Pre-filling rename with:", feed.external_name);
      } else {
        console.warn("Dialog: Feed to rename not found.");
      }
    } else if (dialogType === "create") {
      setFeedName("");
      console.log("Dialog: Clearing feed name for new creation.");
    }
  }, [dialogType, renameFeedId, feeds]);

  return (
    <Box>
      {/* Player auswählen - ohne Box */}
      {!adminPlayer && (
        <Box sx={{ mb: 2 }}>
          <PlayerSelector
            userId={user?.id || null}
            value={selectedPlayer}
            onChange={setSelectedPlayer}
            disabled={false}
          />
        </Box>
      )}
      {adminPlayer && (
        <Typography variant="subtitle2" sx={{ mb: 2 }}>
          {t("feeds.player")} <b>{adminPlayer}</b>
        </Typography>
      )}
      {/* Layout für große Screens: 1/3 links, 2/3 rechts */}
      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          gap: 2,
          flexDirection: { xs: "column", md: "row" },
          alignItems: "flex-start",
        }}
      >
        {/* Linkes Drittel */}
        <Box
          sx={{
            width: { xs: "100%", md: "33.333%" },
          }}
        >
          {/* Playlists verwalten */}
          <Paper sx={{ p: 1.5 }}>
            <Box display="flex" alignItems="center" mb={1.5}>
              <Typography variant="subtitle1" flexGrow={1}>
                {t("feeds.feedsSectionTitle")}
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setDialogOpen(true);
                  setDialogType("create");
                  setFeedName("");
                }}
                disabled={!selectedPlayer}
              >
                {t("feeds.createFeedButton")}
              </Button>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
              {t("feeds.managePlaylistTitle")}
            </Typography>
            <Box
              sx={{
                border: "1px solid rgba(0, 0, 0, 0.12)",
                borderRadius: 1,
                p: 1,
                minHeight: 200,
                maxHeight: 600,
                overflowY: "auto",
              }}
            >
              {feeds.length === 0 && selectedPlayer && (
                <Typography sx={{ my: 2, color: "text.secondary" }}>
                  {t("feeds.noFeedsAvailable")}
                </Typography>
              )}
              {feeds.map((feed: Feed) => (
                <Box
                  key={feed.id}
                  sx={{
                    background:
                      selectedFeedId === feed.id ? "#eeeeee" : "transparent",
                    borderRadius: 2,
                    px: 2,
                    py: 0.5,
                    my: 0.25,
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    console.log("Feed list item clicked:", feed.id, feed.external_name);
                    setSelectedFeedId(feed.id);
                    setSelectedFeedName(feed.external_name);
                  }}
                >
                  <Typography variant="body1" flexGrow={1} sx={{ fontSize: '0.875rem' }}>
                    {feed.external_name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDialogOpen(true);
                      setDialogType("rename");
                      setFeedName(feed.external_name);
                      setRenameFeedId(feed.id);
                    }}
                    title={t("feeds.renameButton")}
                    sx={{ mr: 0.5 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteFeed(feed.id);
                    }}
                    title={t("feeds.deleteButton")}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Paper>
        </Box>

        {/* Rechte zwei Drittel */}
        <Box
          sx={{
            width: { xs: "100%", md: "66.666%" },
          }}
        >
          {/* Drag&Drop-Bereich */}
          {selectedPlayer && selectedFeedId && (
            <Paper sx={{ p: 1.5 }}>
              <Box display="flex" alignItems="center" mb={1.5}>
                <Typography variant="subtitle1" flexGrow={1}>
                  {t("feeds.editFeedTitle")}{" "}
                  <b>{selectedFeedName || t("feeds.noFeedSelected")}</b>{" "}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleStartFeed}
                  disabled={!selectedFeedId}
                >
                  {t("feeds.startFeedButton")}
                </Button>
              </Box>
              <DragDropContext onDragEnd={onDragEnd}>
                <Box sx={{ display: "flex", gap: 2, alignItems: 'stretch' }}>
                  {/* Verfügbare Inhalte - ohne Paper */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 300 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      {t("feeds.availableContentTitle")}{" "}
                    </Typography>
                    <Droppable droppableId="available">
                      {(provided) => {
                        const groupedContent = groupAvailableContent(available);
                        let globalIndex = 0;
                        
                        // Berechne globalen Index für alle Items
                        const allItems: Array<{ item: Content; index: number }> = [];
                        groupedContent.forEach((group) => {
                          group.categories.forEach((catGroup) => {
                            catGroup.items.forEach((item) => {
                              allItems.push({ item, index: globalIndex++ });
                            });
                          });
                        });
                        
                        if (available.length === 0) {
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              style={{ ...groupedListStyle, flex: 1, display: 'flex', flexDirection: 'column' }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {t("feeds.noMoreContentAvailable")}{" "}
                              </Typography>
                              {provided.placeholder}
                            </div>
                          );
                        }
                        
                        // Bereite Tabs vor
                        const superCategories = groupedContent.map(group => group.superCategory);
                        const selectedSuperCategory = superCategories[selectedSuperCategoryTab] ?? null;
                        const selectedGroup = groupedContent.find(group => group.superCategory === selectedSuperCategory) || groupedContent[0];
                        
                        return (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{ ...groupedListStyle, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                          >
                            <Tabs
                              value={selectedSuperCategoryTab}
                              onChange={(event, newValue) => setSelectedSuperCategoryTab(newValue)}
                              variant="scrollable"
                              scrollButtons="auto"
                              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                            >
                              {superCategories.map((superCat, index) => (
                                <Tab
                                  key={superCat || `tab-${index}`}
                                  label={superCat || t("feeds.otherCategory")}
                                />
                              ))}
                            </Tabs>
                            
                            {/* Inhalt des ausgewählten Tabs */}
                            <Box sx={{ flex: 1, overflowY: 'auto' }}>
                              {selectedGroup && selectedGroup.categories.map((catGroup) => (
                                <Box key={catGroup.category || 'no-category'} sx={{ mb: 1.5 }}>
                                  {catGroup.category && (
                                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                                      {catGroup.category}
                                    </Typography>
                                  )}
                                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1 }}>
                                    {catGroup.items.map((item: Content, localIndex: number) => {
                                      const itemWithIndex = allItems.find(entry => entry.item.file_name === item.file_name);
                                      const currentIndex = itemWithIndex?.index ?? 0;
                                      // Eindeutige draggableId für Items in "available", damit sie sich von denen in "feedContents" unterscheiden
                                      const uniqueDraggableId = `available-${item.file_name}-${currentIndex}`;
                                      return (
                                        <Draggable
                                          key={`${item.file_name}-${currentIndex}`}
                                          draggableId={uniqueDraggableId}
                                          index={currentIndex}
                                        >
                                          {(prov, snap) => (
                                            <div
                                              ref={prov.innerRef}
                                              {...prov.draggableProps}
                                              {...prov.dragHandleProps}
                                              style={{ ...itemStyle(snap.isDragging), ...prov.draggableProps.style }}
                                            >
                                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: '100%', position: 'relative' }}>
                                                {showThumbnails ? (
                                                  <>
                                                    <ContentThumbnail item={item} />
                                                    {getDisplayTitle(item) && (
                                                      <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '0.875rem' }}>
                                                        {getDisplayTitle(item)}
                                                      </Typography>
                                                    )}
                                                    {/* Icon für Medientyp statt Text */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                      {isVideo(item) && (
                                                        <PlayArrowIcon sx={{ fontSize: '1.2rem', color: '#999' }} />
                                                      )}
                                                      {isImage(item) && (
                                                        <ImageIcon sx={{ fontSize: '1.2rem', color: '#999' }} />
                                                      )}
                                                    </Box>
                                                  </>
                                                ) : (
                                                  <>
                                                    {getDisplayTitle(item) && (
                                                      <Typography variant="body1" sx={{ fontSize: '0.875rem' }}>
                                                        {getDisplayTitle(item)}
                                                      </Typography>
                                                    )}
                                                    {/* Icon für Medientyp statt Text */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                      {isVideo(item) && (
                                                        <PlayArrowIcon sx={{ fontSize: '1.2rem', color: '#999' }} />
                                                      )}
                                                      {isImage(item) && (
                                                        <ImageIcon sx={{ fontSize: '1.2rem', color: '#999' }} />
                                                      )}
                                                    </Box>
                                                  </>
                                                )}
                                                
                                                {/* Wiedergabelänge für Videos unten rechts */}
                                                {isVideo(item) && item.duration && (
                                                  <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
                                                    <Typography variant="caption" sx={{ color: "#999", fontSize: '0.75rem' }}>
                                                      {formatDuration(item.duration)}
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </Box>
                                            </div>
                                          )}
                                        </Draggable>
                                      );
                                    })}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                            {provided.placeholder}
                          </div>
                        );
                      }}
                    </Droppable>
                  </Box>
                  {/* Feed-Inhalte - ohne Paper */}
                  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 300, position: 'relative' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                      {t("feeds.feedContentTitle")}{" "}
                    </Typography>
                    <Droppable droppableId="feedContents">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{ ...listStyle, flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 40, display: 'flex', flexDirection: 'column' }}
                        >
                          {feedContents.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                              {t("feeds.noContentInFeed")}{" "}
                            </Typography>
                          ) : (
                            feedContents.map((item: Content, idx: number) => (
                              <Draggable
                                key={`feedContents-${item.file_name}-${idx}`}
                                draggableId={`feedContents-${item.file_name}-${idx}`}
                                index={idx}
                              >
                                {(prov, snap) => {
                                  const itemId = `feedContents-${item.file_name}-${idx}`;
                                  const currentDuration = displayDurations.get(itemId) || item.display_duration || 5000;
                                  const { minutes, seconds } = msToMinutesSeconds(currentDuration);
                                  const timeString = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
                                  
                                  return (
                                    <div
                                      ref={prov.innerRef}
                                      {...prov.draggableProps}
                                      {...prov.dragHandleProps}
                                      style={{ ...itemStyle(snap.isDragging), ...prov.draggableProps.style }}
                                    >
                                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, width: '100%', position: 'relative' }}>
                                        {/* Content mit Thumbnail */}
                                        {showThumbnails && <ContentThumbnail item={item} />}
                                        {getDisplayTitle(item) && (
                                          <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '0.875rem' }}>
                                            {getDisplayTitle(item)}
                                          </Typography>
                                        )}
                                        
                                        {/* Ordnungsnummer, Icon und Eingabefelder in einer Zeile unter dem Thumbnail/Caption */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', width: '200px', position: 'relative' }}>
                                          {/* Ordnungsnummer links */}
                                          <Typography variant="body2" sx={{ fontSize: '0.75rem', width: '20px', textAlign: 'left' }}>
                                            {idx + 1}
                                          </Typography>
                                          
                                          {/* Icon für Medientyp absolut mittig */}
                                          <Box sx={{ 
                                            position: 'absolute', 
                                            left: '50%', 
                                            transform: 'translateX(-50%)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                          }}>
                                            {isVideo(item) && (
                                              <PlayArrowIcon sx={{ fontSize: '1rem', color: '#999' }} />
                                            )}
                                            {isImage(item) && (
                                              <ImageIcon sx={{ fontSize: '1rem', color: '#999' }} />
                                            )}
                                          </Box>
                                          
                                          {/* Eingabefelder rechts (nur für Bilder) */}
                                          <Box sx={{ display: 'flex', alignItems: 'center', width: '50px', marginLeft: 'auto', justifyContent: 'flex-end' }}>
                                            {/* Kompaktes Zeit-Eingabefeld für Bilder - nur anzeigen, wenn nicht das einzige Element */}
                                            {isImage(item) && feedContents.length > 1 && (
                                              <TextField
                                                type="text"
                                                size="small"
                                                value={timeString}
                                                onChange={(e) => {
                                                  const value = e.target.value.replace(/[^0-9:]/g, '');
                                                  const parts = value.split(':');
                                                  if (parts.length <= 2) {
                                                    const mins = parts[0] ? Math.min(59, parseInt(parts[0]) || 0) : 0;
                                                    const secs = parts[1] ? Math.min(59, parseInt(parts[1]) || 0) : 0;
                                                    handleDisplayDurationChange(itemId, mins, secs);
                                                  }
                                                }}
                                                onBlur={(e) => {
                                                  const value = e.target.value;
                                                  const parts = value.split(':');
                                                  if (parts.length === 2) {
                                                    const mins = Math.min(59, parseInt(parts[0]) || 0);
                                                    const secs = Math.min(59, parseInt(parts[1]) || 0);
                                                    handleDisplayDurationChange(itemId, mins, secs);
                                                    saveDisplayDurationMetadata();
                                                  } else {
                                                    // Fallback: Versuche das Format zu parsen
                                                    const match = value.match(/(\d+):?(\d*)/);
                                                    if (match) {
                                                      const mins = Math.min(59, parseInt(match[1]) || 0);
                                                      const secs = match[2] ? Math.min(59, parseInt(match[2]) || 0) : 0;
                                                      handleDisplayDurationChange(itemId, mins, secs);
                                                      saveDisplayDurationMetadata();
                                                    }
                                                  }
                                                }}
                                                disabled={snap.isDragging}
                                                inputProps={{ 
                                                  style: { 
                                                    width: '35px', 
                                                    padding: '2px 4px', 
                                                    textAlign: 'center', 
                                                    fontSize: '0.65rem' 
                                                  },
                                                  pattern: '[0-9]{1,2}:[0-9]{2}',
                                                  placeholder: 'MM:SS'
                                                }}
                                                sx={{ 
                                                  width: '45px',
                                                  '& .MuiInputBase-input': { 
                                                    fontSize: '0.65rem', 
                                                    py: 0.25,
                                                    textAlign: 'center'
                                                  } 
                                                }}
                                              />
                                            )}
                                          </Box>
                                        </Box>
                                        
                                        {/* Wiedergabelänge für Videos unten rechts */}
                                        {isVideo(item) && item.duration && (
                                          <Box sx={{ position: 'absolute', bottom: 0, right: 0 }}>
                                            <Typography variant="caption" sx={{ color: "#999", fontSize: '0.75rem' }}>
                                              {formatDuration(item.duration)}
                                            </Typography>
                                          </Box>
                                        )}
                                      </Box>
                                    </div>
                                  );
                                }}
                              </Draggable>
                            ))
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                    
                    {/* Gesamtdauer unten rechts */}
                    {feedContents.length > 0 && (() => {
                      const totalDuration = calculateTotalDuration();
                      return (
                        <Box sx={{ position: 'absolute', bottom: 8, right: 8 }}>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t("feeds.totalDuration")}: {totalDuration === null ? t("feeds.unlimited") : formatDurationFromMs(totalDuration)}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                </Box>
              </DragDropContext>
            </Paper>
          )}
          {!selectedPlayer && (
            <Typography sx={{ mt: 4, color: "text.secondary" }}>
              {t("feeds.selectPlayerPrompt")}
            </Typography>
          )}
          {selectedPlayer && !selectedFeedId && feeds.length > 0 && (
            <Typography sx={{ mt: 4, color: "text.secondary" }}>
              {t("feeds.selectFeedPrompt")}
            </Typography>
          )}
        </Box>
      </Box>
      {/* Feed anlegen/umbenennen Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>
          {dialogType === "create"
            ? t("feeds.dialogCreateTitle")
            : t("feeds.dialogRenameTitle")}{" "}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label={t("feeds.feedNameLabel")}
            fullWidth
            value={feedName}
            onChange={(e) => setFeedName(e.target.value)}
            sx={{ mt: 2 }}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                if (dialogType === "create") handleCreateFeed();
                else handleRenameFeed();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
          <Button
            onClick={dialogType === "create" ? handleCreateFeed : handleRenameFeed}
            disabled={!feedName.trim()}
          >
            {dialogType === "create" ? t("common.create") : t("common.save")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Bestätigungsdialog beim Löschen */}
      <Dialog
        open={confirmDeleteFeedDialogOpen}
        onClose={handleCancelDeleteFeed}
        aria-labelledby="confirm-delete-feed-title"
        aria-describedby="confirm-delete-feed-description"
      >
        <DialogTitle id="confirm-delete-feed-title">{t("feeds.confirmDeleteTitle")}</DialogTitle>
        <DialogContent>
          <Typography id="confirm-delete-feed-description">
            {t("feeds.confirmDeleteMessage", { feedName: feedToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDeleteFeed} color="primary">{t("common.cancel")}</Button>
          <Button onClick={handleConfirmDeleteFeed} color="error" autoFocus>{t("common.delete")}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMsg}
        autoHideDuration={3000}
        onClose={() => setSnackbarMsg(null)}
        message={snackbarMsg}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      />
    </Box>
  );
};

export default Feeds;